import { useLayoutEffect, useEffect, useRef } from "react";

// Safari scrolls off the main thread, so a header moved from a scroll handler
// trails the page by a frame or more and twitches. Instead the header is sticky
// inside a rail that ends --header-anchor px down the page: past that point the
// browser itself slides the header off the top, 1:1 with the page. JS only
// moves the anchor when the direction flips, while the header is fully shown or
// fully gone, so the move itself is never visible.
//
// --header-offset (how far the header is off the top) is still published on
// the root for the sticky side panels that sit under the header.
export default function useHeaderOffset(railRef, headerHeight, pinned) {
  const anchorRef = useRef(null);

  useLayoutEffect(() => {
    const rail = railRef.current;
    const root = document.documentElement;

    // iOS rubber-bands past both ends of the page; the bounce back would
    // otherwise read as a scroll in the opposite direction.
    const readY = () => {
      const maxY = Math.max(root.scrollHeight - window.innerHeight, 0);
      return Math.min(Math.max(window.scrollY, 0), maxY);
    };

    let lastY = readY();
    let lastOffset = null;

    const setAnchor = (anchor) => {
      if (anchor === anchorRef.current) return;
      anchorRef.current = anchor;
      rail.style.setProperty("--header-anchor", `${anchor}px`);
    };

    const setOffset = (offset) => {
      if (offset === lastOffset) return;
      lastOffset = offset;
      root.style.setProperty("--header-offset", `${offset}px`);
    };

    // Only flips once the header is fully off screen, so its links leave the
    // tab order without cutting it off mid-slide.
    const setHidden = (hidden) => {
      if (hidden) rail.dataset.hidden = "";
      else delete rail.dataset.hidden;
    };

    if (pinned) {
      anchorRef.current = null;
      setOffset(0);
      setHidden(false);
      return;
    }

    setAnchor(anchorRef.current ?? lastY);
    setOffset(Math.min(Math.max(lastY - anchorRef.current, 0), headerHeight));

    const onScroll = () => {
      const y = readY();
      const previousY = lastY;
      lastY = y;

      if (y > previousY) setAnchor(Math.min(anchorRef.current, previousY));
      if (y < previousY) setAnchor(Math.max(anchorRef.current, previousY - headerHeight));

      const offset = Math.min(Math.max(y - anchorRef.current, 0), headerHeight);
      setOffset(offset);

      if (y !== previousY) setHidden(y > previousY && headerHeight > 0 && offset >= headerHeight);
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [railRef, headerHeight, pinned]);

  useEffect(() => () => document.documentElement.style.removeProperty("--header-offset"), []);
}
